import React, { useState } from 'react';
import Modal from 'react-modal';
import env from "react-dotenv";
import getUserId from '../utils/getUserId';

let RATE_URL = `${env.API_URL}/rateDime`;
let DELETE_URL = `${env.API_URL}/deleteDime`;

Modal.setAppElement('#root');

export default function RateUserDime(props) {
  const [value, setValue] = useState(10.0);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [serverResponse, setServerResponse] = useState('');
  const [isRating, setIsRating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  
  // Use ref to track timeout for cleanup
  const toastTimeoutRef = React.useRef(null);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []); // NEW: For subtle feedback

  const handleChange = (e) => {
    setValue(parseFloat(e.target.value));
  };

  const onSubmitRate = () => {
    const userId = getUserId();

    if (!userId) {
      alert("Error: User ID missing. Please sign in again.");
      return;
    }

    setIsRating(true);

    fetch(RATE_URL, {
      method: "POST",
      credentials: 'include',
      headers: {
        "Content-Type": "application/json",
        "Authorization": env.API_KEY,
      },
      body: JSON.stringify({
        rating: value,
        dime_id: props.Id,
      }),
    })
      .then(async (response) => {
        let data;
        try {
          data = await response.json();
        } catch {
          data = null;
        }

        if (!response.ok) {
          // Show modal ONLY for errors
          setServerResponse("Error submitting rating.");
          setModalIsOpen(true);
          setIsRating(false);
          return;
        }

        const msg =
          data?.message ||
          data?.status ||
          data?.result ||
          (typeof data === "string" ? data : "");

        const messageStr = String(msg).toLowerCase();

        const isAlreadyRated =
          messageStr.includes("already rated") ||
          messageStr.includes("already") ||
          data?.already === true;

        // CHANGED: Only show modal for "already rated", otherwise just update silently
        if (isAlreadyRated) {
          setServerResponse("Already rated this dime");
          setModalIsOpen(true);
          setIsRating(false);
        } else {
          // SUCCESS: Show subtle toast and refresh immediately
          setShowSuccessToast(true);
          setIsRating(false);
          
          // Hide toast after 2 seconds - store in ref for cleanup
          toastTimeoutRef.current = setTimeout(() => setShowSuccessToast(false), 2000);
          
          // Trigger immediate refresh for smooth UX
          if (props.onRatingSubmitted) {
            props.onRatingSubmitted();
          }
          
          if (props.setTest) {
            props.setTest(value);
          }
        }
      })
      .catch(() => {
        setServerResponse("Error submitting rating.");
        setModalIsOpen(true);
        setIsRating(false);
      });
  };

  const onSubmitDelete = () => {
    if (!window.confirm('Are you sure you want to delete this dime?')) return;

    const userId = getUserId();

    if (!userId) {
      alert("Error: User ID missing. Please sign in again.");
      return;
    }

    setIsDeleting(true);

    fetch(DELETE_URL, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': env.API_KEY
      },
      body: JSON.stringify({
        image_key: props.ImageKey,
        dime_id: props.Id
      })
    })
      .then(async (response) => {
        if (response.status === 403) {
          throw new Error('Forbidden: You do not have permission to delete this dime');
        }

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || "Delete failed.");
        }

        // For delete, we DO show modal since it's a destructive action
        setServerResponse("Dime deleted successfully! Click Close to refresh.");
        setModalIsOpen(true);
        setIsDeleting(false);

        sessionStorage.setItem('dime-just-deleted', 'true');

        if (props.setTest) props.setTest();
      })
      .catch((err) => {
        setServerResponse(err.message || "Error deleting dime. Please try again.");
        setModalIsOpen(true);
        setIsDeleting(false);
      });
  };

  const getRatingText = (val) => {
    if (val < 3) return 'Not Great';
    if (val < 5) return 'Okay';
    if (val < 7) return 'Good';
    if (val < 9) return 'Great';
    return 'Amazing!';
  };

  const pct = Math.min(100, Math.max(0, value * 10));

  return (
    <>
      <div className="rate-dime-container">
        {/* Rating Display */}
        <div className="rating-display">
          <div className="rating-circle">
            <span className="rating-value">{value.toFixed(1)}</span>
          </div>
          <p className="rating-text">{getRatingText(value)}</p>
        </div>

        {/* Slider */}
        <div className="slider-container">
          <input
            type="range"
            min="0"
            max="10"
            step="0.1"
            value={value}
            onChange={handleChange}
            className="rating-slider"
            disabled={isDeleting}
            style={{
              background: `linear-gradient(to right,
                #3b82f6 0%,
                #1d4ed8 ${pct}%,
                #e5e7eb ${pct}%,
                #e5e7eb 100%)`,
            }}
          />
          <div className="slider-labels">
            <span>0</span>
            <span>5</span>
            <span>10</span>
          </div>
        </div>

        {/* Submit Button */}
        <button
          onClick={onSubmitRate}
          disabled={isRating || isDeleting}
          className="submit-btn"
        >
          {isRating ? (
            <>
              <div className="spinner" />
              <span>Submitting...</span>
            </>
          ) : (
            <span>Submit Rating</span>
          )}
        </button>

        {/* Delete Button */}
        <div className="action-buttons">
          <button
            onClick={onSubmitDelete}
            className="action-btn delete-btn"
            type="button"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <div className="spinner-small" />
                <span>Deleting...</span>
              </>
            ) : (
              <span>Delete Dime</span>
            )}
          </button>
        </div>
      </div>

      {/* NEW: Success Toast - subtle feedback without blocking */}
      {showSuccessToast && (
        <div className="success-toast">
          <svg className="toast-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
          <span>Rating updated!</span>
        </div>
      )}

      {/* Modal - Only for errors, "already rated", and delete confirmation */}
      {modalIsOpen && (
        <div className="modal-overlay" onClick={() => !isDeleting && setModalIsOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon">
              <svg className="checkmark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="modal-title">Status</h3>
            <p className="modal-message">{serverResponse}</p>
            <button onClick={() => {
              setModalIsOpen(false);
              if (serverResponse.toLowerCase().includes('deleted successfully')) {
                localStorage.removeItem('p10-userdimes-state');
                sessionStorage.setItem('dime-just-deleted', 'true');
                window.location.reload();
              }
            }} className="modal-close-btn">
              Close
            </button>
          </div>
        </div>
      )}

      <style>{`
        .rate-dime-container {
          padding: 16px;
          background: white;
          border-radius: 8px;
          max-width: 340px;
          margin: 0 auto;
        }

        .rating-display {
          text-align: center;
          margin-bottom: 16px;
        }

        .rating-circle {
          width: 72px;
          height: 72px;
          margin: 0 auto 8px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
        }

        .rating-value {
          font-size: 28px;
          font-weight: 700;
          color: white;
        }

        .rating-text {
          font-size: 13px;
          color: #6b7280;
          margin: 0;
          font-weight: 600;
        }

        .slider-container {
          margin-bottom: 16px;
        }

        .rating-slider {
          width: 100%;
          height: 6px;
          border-radius: 3px;
          background: linear-gradient(to right,
            #3b82f6 0%,
            #1d4ed8 50%,
            #e5e7eb 50%,
            #e5e7eb 100%);
          outline: none;
          -webkit-appearance: none;
          cursor: pointer;
        }

        .rating-slider:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .rating-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.35);
          transition: transform 0.15s;
        }

        .rating-slider::-webkit-slider-thumb:hover {
          transform: scale(1.15);
        }

        .rating-slider::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.35);
          transition: transform 0.15s;
        }

        .slider-labels {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          color: #9ca3af;
          margin-top: 6px;
        }

        .submit-btn {
          width: 100%;
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          color: white;
          border: none;
          padding: 10px 14px;
          border-radius: 10px;
          font-weight: 700;
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          box-shadow: 0 6px 18px rgba(59, 130, 246, 0.18);
          margin-bottom: 10px;
          transition: all 0.2s;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 20px rgba(59, 130, 246, 0.25);
        }

        .submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid white;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        .spinner-small {
          width: 14px;
          height: 14px;
          border: 2px solid currentColor;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        @keyframes spin { to { transform: rotate(360deg); } }

        .action-buttons {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .action-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 8px 12px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.15s;
          border: 1.5px solid;
          background: white;
        }

        .action-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .delete-btn {
          color: #dc2626;
          border-color: #fecaca;
        }

        .delete-btn:hover:not(:disabled) {
          background: #fef2f2;
        }

        /* NEW: Success Toast - subtle, non-blocking feedback */
        .success-toast {
          position: fixed;
          bottom: 24px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(135deg, #34d399, #10b981);
          color: white;
          padding: 12px 20px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 8px;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
          z-index: 1001;
          font-weight: 600;
          font-size: 14px;
          animation: slideUp 0.3s ease-out;
        }

        .toast-icon {
          width: 20px;
          height: 20px;
          stroke-width: 2.5;
        }

        @keyframes slideUp {
          from {
            transform: translateX(-50%) translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }

        /* Modal styles */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.6);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 16px;
          animation: fadeIn 0.2s;
        }

        .modal-content {
          background: white;
          border-radius: 16px;
          padding: 28px;
          max-width: 420px;
          width: 100%;
          text-align: center;
          box-shadow: 0 20px 40px rgba(0,0,0,0.3);
          animation: zoomIn 0.2s;
        }

        .modal-icon {
          width: 56px;
          height: 56px;
          background: linear-gradient(135deg,#34d399,#10b981);
          border-radius: 50%;
          margin: 0 auto 16px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .checkmark {
          width: 28px;
          height: 28px;
          color: white;
        }

        .modal-title {
          font-size: 20px;
          font-weight: 700;
          margin: 0 0 8px;
          color: #111827;
        }

        .modal-message {
          color: #6b7280;
          margin-bottom: 18px;
          line-height: 1.5;
        }

        .modal-close-btn {
          width: 100%;
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          color: white;
          border: none;
          padding: 10px 14px;
          border-radius: 10px;
          font-weight: 700;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .modal-close-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes zoomIn {
          from {
            transform: scale(0.95);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}